import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  /** Label rendered above the field. */
  label?: ReactNode;
  /** Helper text rendered below the field when no error is set. */
  helperText?: ReactNode;
  /** Error message rendered below the field; when set, the field gets an error border. */
  error?: ReactNode;
  /** Element rendered inside the field at the start (icon, prefix). */
  prefix?: ReactNode;
  /** Element rendered inside the field at the end (icon, suffix). */
  suffix?: ReactNode;
  /** Override the field wrapper class. */
  containerClassName?: string;
}

const fieldBase =
  'flex h-10 w-full items-center gap-2 rounded-card border bg-bg-input px-3 text-sm text-white ' +
  'transition-colors focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 ' +
  'focus-within:ring-offset-bg';

/**
 * Text input primitive with label, helper/error message and prefix/suffix slots.
 *
 * The visual focus ring is rendered on the wrapper so prefix/suffix nodes share
 * the same outline. The native `<input>` itself loses its border and just
 * inherits sizing — focus styling is delegated to `focus-within` on the wrapper.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    helperText,
    error,
    prefix,
    suffix,
    containerClassName,
    className,
    id,
    disabled,
    ...rest
  },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const messageId = error || helperText ? `${inputId}-msg` : undefined;
  const hasError = Boolean(error);

  return (
    <div className={cn('flex flex-col gap-1.5', containerClassName)}>
      {label ? (
        <label htmlFor={inputId} className="text-sm font-medium text-white">
          {label}
        </label>
      ) : null}
      <div
        className={cn(
          fieldBase,
          hasError ? 'border-danger' : 'border-border',
          disabled && 'opacity-60 cursor-not-allowed',
        )}
      >
        {prefix ? <span className="shrink-0 text-muted">{prefix}</span> : null}
        <input
          ref={ref}
          id={inputId}
          disabled={disabled}
          aria-invalid={hasError || undefined}
          aria-describedby={messageId}
          className={cn(
            'w-full bg-transparent text-white placeholder:text-muted outline-none',
            'disabled:cursor-not-allowed',
            className,
          )}
          {...rest}
        />
        {suffix ? <span className="shrink-0 text-muted">{suffix}</span> : null}
      </div>
      {error ? (
        <p id={messageId} className="text-xs text-danger">
          {error}
        </p>
      ) : helperText ? (
        <p id={messageId} className="text-xs text-muted">
          {helperText}
        </p>
      ) : null}
    </div>
  );
});
